import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/core/auth/hooks/useAuth";

/**
 * Opens a validated Stripe Checkout or Customer Portal URL in the same browser tab.
 * Billing only runs on the standalone web page; the native app never opens Stripe billing.
 */
export function useOpenBillingUrl(scope: string) {
  const { getAuthGeneration } = useAuth();
  const pending = useRef<{ isCurrent: () => boolean } | null>(null);
  const lifetime = useRef<{ active: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    const current = { active: true };
    lifetime.current = current;
    const restored = (event: PageTransitionEvent) => {
      if (!event.persisted || !pending.current?.isCurrent()) return;
      pending.current = null;
      setBusy(false);
      setMessage("Returned from Stripe. Your plan below comes from Froppio, not from the browser.");
    };
    window.addEventListener("pageshow", restored);
    return () => {
      current.active = false;
      window.removeEventListener("pageshow", restored);
      pending.current = null;
    };
  }, [scope]);

  const open = async (createUrl: () => Promise<string>) => {
    if (pending.current) return;
    const owner = lifetime.current;
    const generation = getAuthGeneration();
    const isCurrent = () => !!owner?.active && owner === lifetime.current && generation === getAuthGeneration();
    const current = { isCurrent };
    pending.current = current;
    setBusy(true);
    setMessage("");
    try {
      const url = await createUrl();
      if (pending.current !== current || !isCurrent()) return;
      if (window.self !== window.top) throw new Error("Billing cannot open in a frame.");
      window.location.assign(url);
    } catch {
      if (pending.current !== current || !isCurrent()) return;
      pending.current = null;
      setBusy(false);
      setMessage("Couldn't open Stripe. Refresh your plan and try again.");
    }
  };
  return { open, busy, message };
}
